import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import * as R from 'ramda'
import { Button, Form, Divider } from 'semantic-ui-react'

import LoginEmailInput from './EmailInput'
import PasswordInput from './PasswordInput'
import Warning from './Warning'

import 'typeface-roboto'
import './style.css'

import { convertQueryParamStringToObject } from 'libs/convertQueryParamStringToObject'
import SocialLoginButton from 'shared/components/SocialLoginButton'
import { initializeOAuthFlow } from 'containers/SignUpFormV2/utils'

import constants from 'config/constants'

export default function Login({ search, error, availableLoginMethods, onLogin, loading }) {
  const queryParams = convertQueryParamStringToObject(search)
  const handleGoogleLogin = () => initializeOAuthFlow(queryParams.redirect_uri, 'GoogleOAuth')
  const handleMicrosoftLogin = () => initializeOAuthFlow(queryParams.redirect_uri, 'MicrosoftOAuth')

  const loginMethodsMap = {
    email_password: 'Email and password',
    sso: 'SSO',
  }

  const missingMethods = ['email_password', 'social_login'].filter(
    (method) => !availableLoginMethods.includes(method)
  )
  const availableMethods = availableLoginMethods.filter((method) => method !== 'sso')
  const hasSso = availableLoginMethods.includes('sso')

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (error) {
      setPassword('')
    }
  }, [error])

  function handleSubmit(e) {
    e.preventDefault()
    const username = e.target.email.value
    const password = e.target.password.value
    onLogin(username, password)
  }

  const errorType = /** @type {string | undefined} */ (
    R.keys(error).find((key) => {
      return error[key]
    })
  )

  // We use this to separate Google and Microsoft methods for easier handling
  const expandMethods = (methods) => {
    return methods.flatMap((method) =>
      method === 'social_login' ? ['Google', 'Microsoft'] : [loginMethodsMap[method]]
    )
  }

  const formatMethodsList = (methods, conjunction = 'or') => {
    if (methods.length === 1) {
      return methods[0]
    }
    if (methods.length === 2) {
      return `${methods[0]} ${conjunction} ${methods[1]}`
    }
    return `${methods.slice(0, -1).join(', ')}, ${conjunction} ${methods[methods.length - 1]}`
  }

  // Warning message string prep
  const availableMethodsDisplay = formatMethodsList([
    ...expandMethods(availableMethods),
    ...(hasSso ? [loginMethodsMap.sso] : []),
  ])

  const missingMethodsString = formatMethodsList(expandMethods(missingMethods), 'and')

  const loginMethodText = missingMethods.length > 1 ? 'methods' : 'method'

  return (
    <>
      <Warning
        errorType={errorType}
        availableLoginMethods={availableLoginMethods}
        missingMethodsString={missingMethodsString}
        loginMethodText={loginMethodText}
        availableMethodsDisplay={availableMethodsDisplay}
      />
      <Form onSubmit={handleSubmit}>
        <LoginEmailInput value={username} onChange={setUsername} />
        <PasswordInput value={password} onChange={setPassword} />
        <Button
          primary
          fluid
          size="large"
          type="submit"
          id="login_button"
          className="login-submit-button"
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Log in'}
        </Button>
      </Form>

      <Divider horizontal>
        <p className="text-divider">Or</p>
      </Divider>

      <div className="login-social-stack">
        <SocialLoginButton
          onClick={handleGoogleLogin}
          icon={constants.googleLoginIcon}
          iconAlt="google-login-button"
        >
          Log in with Google
        </SocialLoginButton>
        <SocialLoginButton
          onClick={handleMicrosoftLogin}
          icon={constants.microsoftLoginIcon}
          iconAlt="microsoft-login-button"
        >
          Log in with Microsoft
        </SocialLoginButton>
        <SocialLoginButton
          to={{ pathname: '/login/sso/', search }}
          icon={constants.ssoLoginIcon}
          iconAlt="sso-login-button"
        >
          Log in with SSO
        </SocialLoginButton>
      </div>

      <div className="login-footer">
        <Link to={{ pathname: '/reset-password/', search }}>Forgot your password?</Link>
        <span>
          New to us? &nbsp; <Link to={{ pathname: '/signup/', search }}>Sign up</Link>
        </span>
      </div>
    </>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/Login/index.js